/**
 * Permission catalogue — the single source of truth.
 * Format: `resource.action`. Referenced by constant, never as a bare string in route files.
 */

export const P = {
  // --- administration -------------------------------------------------------
  USER_VIEW: 'user.view',
  USER_CREATE: 'user.create',
  USER_UPDATE: 'user.update',
  USER_SUSPEND: 'user.suspend',
  USER_ASSIGN_SECTION: 'user.assign_section',

  ROLE_VIEW: 'role.view',
  ROLE_CREATE: 'role.create',
  ROLE_UPDATE: 'role.update',
  ROLE_DELETE: 'role.delete',

  SECTION_VIEW: 'section.view',
  SECTION_UPDATE: 'section.update',
  SECTION_SETTINGS: 'section.settings',

  SETTINGS_VIEW: 'settings.view',
  SETTINGS_UPDATE: 'settings.update',

  /**
   * Currencies (docs/02 §11).
   *
   * `view` is deliberately not an administrative grant: every screen that shows or takes
   * an amount needs the list to know how many decimals the amount has and what to render
   * beside it, so it sits with the shell alongside `section.view`. `manage` is the
   * administrative half — creating a currency, re-rating it, or moving the base — and it
   * is checked globally, because a currency is the group's, not a section's.
   */
  CURRENCY_VIEW: 'currency.view',
  CURRENCY_MANAGE: 'currency.manage',

  AUDIT_VIEW: 'audit.view',

  // --- master data ----------------------------------------------------------
  PARTY_VIEW: 'party.view',
  PARTY_CREATE: 'party.create',
  PARTY_UPDATE: 'party.update',
  PARTY_DELETE: 'party.delete',

  // --- accounting -----------------------------------------------------------
  ACCOUNT_VIEW: 'account.view',
  ACCOUNT_CREATE: 'account.create',
  ACCOUNT_UPDATE: 'account.update',

  JOURNAL_VIEW: 'journal.view',
  JOURNAL_CREATE: 'journal.create',
  JOURNAL_POST: 'journal.post',
  JOURNAL_REVERSE: 'journal.reverse',

  PERIOD_VIEW: 'period.view',
  PERIOD_LOCK: 'period.lock',
  PERIOD_CLOSE: 'period.close',
  PERIOD_REOPEN: 'period.reopen',

  // --- treasury -------------------------------------------------------------
  SAFE_VIEW: 'safe.view',
  SAFE_CREATE: 'safe.create',
  SAFE_UPDATE: 'safe.update',
  SAFE_VIEW_BALANCE: 'safe.view_balance',
  SAFE_ADJUST: 'safe.adjust',

  SAFE_RECON_VIEW: 'safe_reconciliation.view',
  SAFE_RECON_CREATE: 'safe_reconciliation.create',
  SAFE_RECON_APPROVE: 'safe_reconciliation.approve',

  // --- documents ------------------------------------------------------------
  INVOICE_VIEW: 'invoice.view',
  INVOICE_CREATE: 'invoice.create',
  INVOICE_UPDATE: 'invoice.update',
  INVOICE_SUBMIT: 'invoice.submit',
  INVOICE_APPROVE: 'invoice.approve',
  INVOICE_POST: 'invoice.post',
  INVOICE_VOID: 'invoice.void',
  INVOICE_DELETE: 'invoice.delete',

  PAYMENT_VIEW: 'payment.view',
  PAYMENT_CREATE: 'payment.create',
  PAYMENT_APPROVE: 'payment.approve',
  PAYMENT_POST: 'payment.post',
  PAYMENT_VOID: 'payment.void',

  EXPENSE_VIEW: 'expense.view',
  EXPENSE_CREATE: 'expense.create',
  EXPENSE_SUBMIT: 'expense.submit',
  EXPENSE_APPROVE: 'expense.approve',
  EXPENSE_POST: 'expense.post',
  EXPENSE_VOID: 'expense.void',

  PAYROLL_VIEW: 'payroll.view',
  PAYROLL_CREATE: 'payroll.create',
  PAYROLL_APPROVE: 'payroll.approve',
  PAYROLL_POST: 'payroll.post',
  PAYROLL_PAY: 'payroll.pay',

  // --- commissions ----------------------------------------------------------
  COMMISSION_PLAN_VIEW: 'commission_plan.view',
  COMMISSION_PLAN_CREATE: 'commission_plan.create',
  COMMISSION_PLAN_UPDATE: 'commission_plan.update',
  COMMISSION_PLAN_DEACTIVATE: 'commission_plan.deactivate',

  COMMISSION_VIEW: 'commission.view',
  COMMISSION_APPROVE: 'commission.approve',
  COMMISSION_CANCEL: 'commission.cancel',
  COMMISSION_PAYOUT: 'commission.payout',

  // --- profit ---------------------------------------------------------------

  PROFIT_VIEW: 'profit.view',
  PROFIT_COMPUTE: 'profit.compute',
  PROFIT_DISTRIBUTE: 'profit.distribute',
  PROFIT_APPROVE: 'profit.approve',

  // --- attachments & reports ------------------------------------------------
  ATTACHMENT_VIEW: 'attachment.view',
  ATTACHMENT_UPLOAD: 'attachment.upload',
  ATTACHMENT_DELETE: 'attachment.delete',

  REPORT_VIEW: 'report.view',
  REPORT_EXPORT: 'report.export',
  /** The key to the group-wide Main Safe view. */
  REPORT_CONSOLIDATED_VIEW: 'report.consolidated.view',

  // --- verticals ------------------------------------------------------------
  PROP_PROPERTY_VIEW: 'properties.property.view',
  PROP_PROPERTY_MANAGE: 'properties.property.manage',
  /**
   * **Property owners** (docs/10 §4.1a) — the people Construction builds for, and the
   * money each has paid in.
   *
   * Filed under `properties.*` because that is Construction's vertical module; the owner
   * register is part of it, not a thing of its own. `manage` is a treasury grant in a
   * sales wrapper for the same reason the treat one is: taking a deposit and refunding it
   * both move cash in the safe.
   */
  PROP_OWNER_VIEW: 'properties.owner.view',
  PROP_OWNER_MANAGE: 'properties.owner.manage',

  PROP_CONTRACT_VIEW: 'properties.contract.view',
  PROP_CONTRACT_MANAGE: 'properties.contract.manage',

  /**
   * **Real Estate treats** — buying a property with the company's own money and selling
   * it on (docs/10 §4.2a).
   *
   * Namespaced `realestate.*` rather than `properties.*` on purpose: the property
   * register moved to Construction with the `properties` module (§4.1), and a Real Estate
   * grant filed under that name would read as permission over Construction's register.
   * Distinct from `brokers.*` for the mirror-image reason — a broker's treat spends *his*
   * capital, and being trusted with one is not being trusted with the other.
   *
   * `manage` is a treasury grant in a sales wrapper: buying takes cash out of the safe.
   */
  RE_TREAT_VIEW: 'realestate.treat.view',
  RE_TREAT_MANAGE: 'realestate.treat.manage',

  /**
   * **Real Estate tenancies** — the rent agreement behind the Tenants register.
   *
   * `realestate.*` for the same reason the treat grants are: `properties.contract.*` reads
   * as authority over Construction's sale and lease contracts, which this is not.
   *
   * Unlike a treat, `manage` sits with the **accountant**. Writing a tenancy moves no cash —
   * it lays out what will be billed, and each period still becomes a *draft* invoice that
   * somebody posts. The grant that spends money here is `invoice.post`, which the accountant
   * already holds or does not, and this one must not become a second way to reach it.
   */
  RE_TENANCY_VIEW: 'realestate.tenancy.view',
  RE_TENANCY_MANAGE: 'realestate.tenancy.manage',

  AG_CONTRACT_VIEW: 'agency.contract.view',
  AG_CONTRACT_MANAGE: 'agency.contract.manage',
  AG_CAMPAIGN_VIEW: 'agency.campaign.view',
  AG_CAMPAIGN_MANAGE: 'agency.campaign.manage',

  BR_PROFILE_VIEW: 'brokers.profile.view',
  BR_PROFILE_MANAGE: 'brokers.profile.manage',
  BR_DEAL_VIEW: 'brokers.deal.view',
  BR_DEAL_MANAGE: 'brokers.deal.manage',
  BR_DEAL_VIEW_MARGIN: 'brokers.deal.view_margin',
  BR_PAYOUT_MANAGE: 'brokers.payout.manage',
  BR_CAPITAL_VIEW: 'brokers.capital.view',
  BR_CAPITAL_MANAGE: 'brokers.capital.manage',

  /**
   * **Cleaner orders** and the expenses entered on them (docs/10 §50).
   *
   * `manage` moves the safe in both directions — saving an order puts the customer's money
   * in, an expense takes the job's costs out — with no approval in between, so it sits with
   * the manager, the same call the treat grants made. The accountant reads.
   */
  CL_ORDER_VIEW: 'cleaner.order.view',
  CL_ORDER_MANAGE: 'cleaner.order.manage',
};

export const ALL_PERMISSIONS = Object.freeze(Object.values(P));

const PERMISSION_SET = new Set(ALL_PERMISSIONS);
export const isValidPermission = (p) => PERMISSION_SET.has(p);

/** Groups used to render the role editor UI. */
export const PERMISSION_GROUPS = [
  {
    key: 'administration',
    permissions: [
      P.USER_VIEW,
      P.USER_CREATE,
      P.USER_UPDATE,
      P.USER_SUSPEND,
      P.USER_ASSIGN_SECTION,
      P.ROLE_VIEW,
      P.ROLE_CREATE,
      P.ROLE_UPDATE,
      P.ROLE_DELETE,
      P.SECTION_VIEW,
      P.SECTION_UPDATE,
      P.SECTION_SETTINGS,
      P.SETTINGS_VIEW,
      P.SETTINGS_UPDATE,
      P.CURRENCY_VIEW,
      P.CURRENCY_MANAGE,
      P.AUDIT_VIEW,
    ],
  },
  { key: 'masterData', permissions: [P.PARTY_VIEW, P.PARTY_CREATE, P.PARTY_UPDATE, P.PARTY_DELETE] },
  {
    key: 'accounting',
    permissions: [
      P.ACCOUNT_VIEW,
      P.ACCOUNT_CREATE,
      P.ACCOUNT_UPDATE,
      P.JOURNAL_VIEW,
      P.JOURNAL_CREATE,
      P.JOURNAL_POST,
      P.JOURNAL_REVERSE,
      P.PERIOD_VIEW,
      P.PERIOD_LOCK,
      P.PERIOD_CLOSE,
      P.PERIOD_REOPEN,
    ],
  },
  {
    key: 'treasury',
    permissions: [
      P.SAFE_VIEW,
      P.SAFE_CREATE,
      P.SAFE_UPDATE,
      P.SAFE_VIEW_BALANCE,
      P.SAFE_ADJUST,
      P.SAFE_RECON_VIEW,
      P.SAFE_RECON_CREATE,
      P.SAFE_RECON_APPROVE,
    ],
  },
  {
    key: 'documents',
    permissions: [
      P.INVOICE_VIEW,
      P.INVOICE_CREATE,
      P.INVOICE_UPDATE,
      P.INVOICE_SUBMIT,
      P.INVOICE_APPROVE,
      P.INVOICE_POST,
      P.INVOICE_VOID,
      P.INVOICE_DELETE,
      P.PAYMENT_VIEW,
      P.PAYMENT_CREATE,
      P.PAYMENT_APPROVE,
      P.PAYMENT_POST,
      P.PAYMENT_VOID,
      P.EXPENSE_VIEW,
      P.EXPENSE_CREATE,
      P.EXPENSE_SUBMIT,
      P.EXPENSE_APPROVE,
      P.EXPENSE_POST,
      P.EXPENSE_VOID,
    ],
  },
  {
    key: 'payroll',
    permissions: [P.PAYROLL_VIEW, P.PAYROLL_CREATE, P.PAYROLL_APPROVE, P.PAYROLL_POST, P.PAYROLL_PAY],
  },
  {
    key: 'commissions',
    permissions: [
      P.COMMISSION_PLAN_VIEW,
      P.COMMISSION_PLAN_CREATE,
      P.COMMISSION_PLAN_UPDATE,
      P.COMMISSION_PLAN_DEACTIVATE,
      P.COMMISSION_VIEW,
      P.COMMISSION_APPROVE,
      P.COMMISSION_CANCEL,
      P.COMMISSION_PAYOUT,
    ],
  },
  {
    key: 'profit',
    permissions: [P.PROFIT_VIEW, P.PROFIT_COMPUTE, P.PROFIT_DISTRIBUTE, P.PROFIT_APPROVE],
  },
  {
    key: 'attachmentsReports',
    permissions: [
      P.ATTACHMENT_VIEW,
      P.ATTACHMENT_UPLOAD,
      P.ATTACHMENT_DELETE,
      P.REPORT_VIEW,
      P.REPORT_EXPORT,
      P.REPORT_CONSOLIDATED_VIEW,
    ],
  },
  {
    key: 'properties',
    permissions: [
      P.PROP_PROPERTY_VIEW,
      P.PROP_PROPERTY_MANAGE,
      P.PROP_CONTRACT_VIEW,
      P.PROP_CONTRACT_MANAGE,
      /* Construction's owners (docs/10 §4.1a). Absent here since they were built, so a role
         author could not grant them without editing a seed. */
      P.PROP_OWNER_VIEW,
      P.PROP_OWNER_MANAGE,
    ],
  },
  {
    /**
     * Real Estate's own two registers — treats (§4.2a) and tenancies (§4.2c).
     *
     * A group of its own rather than rows under `properties`, because that key means
     * Construction's register since §4.1 and a grant listed under it reads as authority
     * there. The treat grants existed and appeared in **no group at all**, which meant the
     * only way to give somebody Real Estate's trading register was to edit a seeded role.
     */
    key: 'realestate',
    permissions: [P.RE_TREAT_VIEW, P.RE_TREAT_MANAGE, P.RE_TENANCY_VIEW, P.RE_TENANCY_MANAGE],
  },
  {
    key: 'agency',
    permissions: [P.AG_CONTRACT_VIEW, P.AG_CONTRACT_MANAGE, P.AG_CAMPAIGN_VIEW, P.AG_CAMPAIGN_MANAGE],
  },
  {
    key: 'brokers',
    permissions: [
      P.BR_PROFILE_VIEW,
      P.BR_PROFILE_MANAGE,
      P.BR_DEAL_VIEW,
      P.BR_DEAL_MANAGE,
      P.BR_DEAL_VIEW_MARGIN,
      P.BR_PAYOUT_MANAGE,
      P.BR_CAPITAL_VIEW,
      P.BR_CAPITAL_MANAGE,
    ],
  },
  {
    key: 'cleaner',
    permissions: [P.CL_ORDER_VIEW, P.CL_ORDER_MANAGE],
  },
];

// ---------------------------------------------------------------------------
// System roles (seeded with isSystem: true) — see docs/03 §3
// ---------------------------------------------------------------------------

const READ_ONLY = ALL_PERMISSIONS.filter((p) => /\.(view|view_balance|view_margin)$/.test(p));

const SECTION_ACCOUNTANT = [
  // Without section.view the core role cannot read its own section or populate the
  // section switcher (docs/03 §5) — it is the floor for every section-scoped role.
  P.SECTION_VIEW,
  // Every screen that renders or accepts an amount needs the currency list to know its
  // decimals — so this is a floor for money-touching roles, not an admin grant.
  P.CURRENCY_VIEW,
  P.PARTY_VIEW,
  P.PARTY_CREATE,
  P.PARTY_UPDATE,
  P.ACCOUNT_VIEW,
  P.JOURNAL_VIEW,
  P.PERIOD_VIEW,
  P.SAFE_VIEW,
  P.SAFE_VIEW_BALANCE,
  P.SAFE_RECON_VIEW,
  P.SAFE_RECON_CREATE,
  P.INVOICE_VIEW,
  P.INVOICE_CREATE,
  P.INVOICE_UPDATE,
  P.INVOICE_SUBMIT,
  P.PAYMENT_VIEW,
  P.PAYMENT_CREATE,
  P.EXPENSE_VIEW,
  P.EXPENSE_CREATE,
  P.EXPENSE_SUBMIT,
  P.PAYROLL_VIEW,
  P.PAYROLL_CREATE,
  P.COMMISSION_PLAN_VIEW,
  P.COMMISSION_VIEW,
  P.PROFIT_VIEW,
  P.ATTACHMENT_VIEW,
  P.ATTACHMENT_UPLOAD,
  P.ATTACHMENT_DELETE,
  P.REPORT_VIEW,
  P.REPORT_EXPORT,
  P.PROP_PROPERTY_VIEW,
  P.PROP_CONTRACT_VIEW,
  P.PROP_OWNER_VIEW,
  P.RE_TREAT_VIEW,
  /*
   * Both halves, unlike the treat above. A tenancy raises no entry of its own — its periods
   * become draft invoices — so writing one is bookkeeping, and the section accountant is who
   * does it.
   */
  P.RE_TENANCY_VIEW,
  P.RE_TENANCY_MANAGE,
  P.AG_CONTRACT_VIEW,
  P.AG_CAMPAIGN_VIEW,
  P.BR_PROFILE_VIEW,
  P.BR_DEAL_VIEW,
  P.BR_CAPITAL_VIEW,
  P.CL_ORDER_VIEW,
];

const SECTION_MANAGER = [
  ...SECTION_ACCOUNTANT,
  P.INVOICE_APPROVE,
  P.INVOICE_POST,
  P.INVOICE_VOID,
  P.PAYMENT_APPROVE,
  P.PAYMENT_POST,
  P.PAYMENT_VOID,
  P.EXPENSE_APPROVE,
  P.EXPENSE_POST,
  P.EXPENSE_VOID,
  P.PAYROLL_APPROVE,
  P.PAYROLL_POST,
  P.PAYROLL_PAY,
  P.COMMISSION_APPROVE,
  P.COMMISSION_CANCEL,
  P.COMMISSION_PAYOUT,
  P.PROFIT_COMPUTE,
  P.PROFIT_DISTRIBUTE,
  /*
   * Freezing a statement and agreeing the share table are decisions, not bookkeeping — so
   * `profit.approve` sits with the manager alongside compute/distribute, and the
   * creator ≠ approver check keeps one person from doing both ends of a distribution.
   */
  P.PROFIT_APPROVE,
  P.SAFE_RECON_APPROVE,
  P.PARTY_DELETE,
  P.PROP_PROPERTY_MANAGE,
  P.PROP_CONTRACT_MANAGE,
  /* Taking an owner's deposit and refunding it both move cash — the manager's, like
     moving a broker's capital (docs/10 §3). */
  P.PROP_OWNER_MANAGE,
  /*
   * Buying a treat spends the company's cash before anybody has approved a document, so
   * it sits with the manager rather than the accountant — the same call §3 made about
   * moving a broker's capital.
   */
  P.RE_TREAT_MANAGE,
  P.RE_TENANCY_VIEW,
  P.RE_TENANCY_MANAGE,
  P.AG_CONTRACT_MANAGE,
  P.AG_CAMPAIGN_MANAGE,
  P.BR_PROFILE_MANAGE,
  P.BR_DEAL_MANAGE,
  P.BR_DEAL_VIEW_MARGIN,
  P.BR_PAYOUT_MANAGE,
  /*
   * Moving a broker's capital is the manager's, not the accountant's (docs/10 §3).
   *
   * `brokers.capital.view` is in `SECTION_ACCOUNTANT` above and comes with it — reading what
   * the group owes somebody is bookkeeping. Taking his money in, handing it back, and buying
   * or selling with it are decisions about somebody else's property, which is why the treat
   * routes ask for this grant rather than `brokers.deal.manage`.
   */
  P.BR_CAPITAL_MANAGE,
  /* An order puts cash in and its expenses take cash out, both on entry (docs/10 §50). */
  P.CL_ORDER_MANAGE,
];

export const SYSTEM_ROLES = [
  {
    key: 'super_admin',
    name: 'Super Administrator',
    nameAr: 'مدير النظام',
    nameCkb: 'بەڕێوەبەری سیستەم',
    scopeType: 'global',
    description: 'Unrestricted access to every section and every setting.',
    permissions: ALL_PERMISSIONS,
  },
  {
    key: 'cfo',
    name: 'Chief Financial Officer',
    nameAr: 'المدير المالي',
    nameCkb: 'بەڕێوەبەری دارایی',
    scopeType: 'global',
    description:
      'All sections: approvals, posting, reversals, period close, consolidated reports, Main Safe.',
    permissions: ALL_PERMISSIONS.filter(
      (p) => ![P.ROLE_CREATE, P.ROLE_DELETE, P.USER_CREATE, P.USER_SUSPEND].includes(p),
    ),
  },
  {
    key: 'main_treasurer',
    name: 'Main Treasurer',
    nameAr: 'أمين الخزينة الرئيسية',
    nameCkb: 'بەرپرسی خەزێنەی سەرەکی',
    scopeType: 'global',
    description: 'Custody of the Main Safe: funding, settlement, reconciliation and the group cash view.',
    permissions: [
      P.SECTION_VIEW,
      P.CURRENCY_VIEW,
      P.PARTY_VIEW,
      P.ACCOUNT_VIEW,
      P.JOURNAL_VIEW,
      P.PERIOD_VIEW,
      P.SAFE_VIEW,
      P.SAFE_CREATE,
      P.SAFE_UPDATE,
      P.SAFE_VIEW_BALANCE,
      P.SAFE_ADJUST,
      P.SAFE_RECON_VIEW,
      P.SAFE_RECON_CREATE,
      P.SAFE_RECON_APPROVE,
      P.PAYMENT_VIEW,
      P.INVOICE_VIEW,
      P.EXPENSE_VIEW,
      P.ATTACHMENT_VIEW,
      P.ATTACHMENT_UPLOAD,
      P.REPORT_VIEW,
      P.REPORT_EXPORT,
      P.REPORT_CONSOLIDATED_VIEW,
    ],
  },
  {
    key: 'group_accountant',
    name: 'Group Accountant',
    nameAr: 'محاسب المجموعة',
    nameCkb: 'ژمێریاری گروپ',
    scopeType: 'global',
    description: 'Reads every section, may create journal entries, runs all reports. No approvals.',
    permissions: [
      ...READ_ONLY,
      P.JOURNAL_CREATE,
      P.ACCOUNT_CREATE,
      P.ACCOUNT_UPDATE,
      P.REPORT_EXPORT,
      P.REPORT_CONSOLIDATED_VIEW,
      P.ATTACHMENT_VIEW,
      P.ATTACHMENT_UPLOAD,
    ],
  },
  {
    key: 'section_manager',
    name: 'Section Manager',
    nameAr: 'مدير القسم',
    nameCkb: 'بەڕێوەبەری بەش',
    scopeType: 'section',
    description: 'Full control of one section including approvals and posting.',
    permissions: [...new Set(SECTION_MANAGER)],
  },
  {
    key: 'section_accountant',
    name: 'Section Accountant',
    nameAr: 'محاسب القسم',
    nameCkb: 'ژمێریاری بەش',
    scopeType: 'section',
    description:
      'Enters invoices, expenses, payments, payroll and attachments for one section. Cannot approve or post.',
    permissions: [...new Set(SECTION_ACCOUNTANT)],
  },
  {
    key: 'section_treasurer',
    name: 'Section Treasurer',
    nameAr: 'أمين صندوق القسم',
    nameCkb: 'بەرپرسی خەزێنەی بەش',
    scopeType: 'section',
    description: 'Custody of one section safe: receipts, disbursements, reconciliation, settlement requests.',
    permissions: [
      P.SECTION_VIEW,
      P.CURRENCY_VIEW,
      P.PARTY_VIEW,
      P.JOURNAL_VIEW,
      P.PERIOD_VIEW,
      P.SAFE_VIEW,
      P.SAFE_VIEW_BALANCE,
      P.SAFE_RECON_VIEW,
      P.SAFE_RECON_CREATE,
      P.PAYMENT_VIEW,
      P.PAYMENT_CREATE,
      P.PAYMENT_POST,
      P.INVOICE_VIEW,
      P.EXPENSE_VIEW,
      P.ATTACHMENT_VIEW,
      P.ATTACHMENT_UPLOAD,
      P.REPORT_VIEW,
      P.REPORT_EXPORT,
    ],
  },
  {
    key: 'broker',
    name: 'Broker',
    nameAr: 'وسيط',
    nameCkb: 'دەلاڵ',
    scopeType: 'section',
    description: 'Sees only their own treats, commissions and capital balance.',
    permissions: [
      P.SECTION_VIEW,
      P.CURRENCY_VIEW,
      P.PARTY_VIEW,
      P.SAFE_VIEW,
      P.SAFE_VIEW_BALANCE,
      P.INVOICE_VIEW,
      P.EXPENSE_VIEW,
      P.EXPENSE_CREATE,
      P.EXPENSE_SUBMIT,
      P.COMMISSION_VIEW,
      P.COMMISSION_PLAN_VIEW,
      P.PROFIT_VIEW,
      P.BR_PROFILE_VIEW,
      P.BR_DEAL_VIEW,
      P.BR_DEAL_MANAGE,
      P.ATTACHMENT_VIEW,
      P.ATTACHMENT_UPLOAD,
      P.REPORT_VIEW,
    ],
  },
  {
    key: 'hr_payroll',
    name: 'HR & Payroll',
    nameAr: 'الموارد البشرية والرواتب',
    nameCkb: 'سەرچاوە مرۆییەکان و مووچە',
    scopeType: 'global',
    description: 'Employees and payroll only.',
    permissions: [
      P.SECTION_VIEW,
      P.CURRENCY_VIEW,
      P.PARTY_VIEW,
      P.PARTY_CREATE,
      P.PARTY_UPDATE,
      P.PAYROLL_VIEW,
      P.PAYROLL_CREATE,
      P.PAYROLL_APPROVE,
      P.PAYROLL_POST,
      P.PAYROLL_PAY,
      P.COMMISSION_VIEW,
      P.ATTACHMENT_VIEW,
      P.ATTACHMENT_UPLOAD,
      P.REPORT_VIEW,
      P.REPORT_EXPORT,
    ],
  },
  {
    key: 'auditor',
    name: 'Auditor',
    nameAr: 'مدقق',
    nameCkb: 'پشکنەر',
    scopeType: 'global',
    description: 'Read-only across the whole group, including the audit log. No mutations at all.',
    permissions: [...READ_ONLY, P.AUDIT_VIEW, P.REPORT_EXPORT, P.REPORT_CONSOLIDATED_VIEW],
  },
];

/** Roles whose members may never approve their own documents (segregation of duties). */
export const SELF_APPROVAL_FORBIDDEN = true;

// ---------------------------------------------------------------------------
// Client-side helpers
// ---------------------------------------------------------------------------

/**
 * @param {{isSuperAdmin?:boolean, global?:string[], bySection?:Record<string,string[]>}} access
 */
export const can = (access, permission, sectionId = null) => {
  if (!access) return false;
  if (access.isSuperAdmin) return true;
  if (access.global?.includes(permission)) return true;
  if (sectionId) return !!access.bySection?.[String(sectionId)]?.includes(permission);
  return Object.values(access.bySection || {}).some((list) => list.includes(permission));
};

export const canAny = (access, permissions, sectionId = null) =>
  permissions.some((p) => can(access, p, sectionId));

export const canAll = (access, permissions, sectionId = null) =>
  permissions.every((p) => can(access, p, sectionId));

export default P;
